import React from 'react'
import * as rn from 'react-native'
import { StyleSheet, useColorScheme } from 'react-native'

import * as c from '../../../common'
import { selectThumbnail, useSelector } from '../../../common'

import Icon from 'react-native-vector-icons/MaterialCommunityIcons'

import * as gs from '../../gStyles'

import Text from '../Text'

export interface ListCustomerProps {
  customerAddress: string
  customerName: string
  hideAvatar?: boolean
  homeRep?: string
  itemKey: string
  lastModified: number
  onPress?: (itemKey: string) => void
  solarCompany: string
  solarRep: string
}

const avatarEdge = 88
const paddingVertical = 12
export const LIST_CUSTOMER_H = avatarEdge + paddingVertical * 2

export default React.memo<Omit<ListCustomerProps, 'onPress'>>(
  function ListCustomerContent({
    customerAddress,
    customerName,
    hideAvatar,
    homeRep: closer,
    itemKey,
    solarCompany,
    solarRep: setter,
  }): React.ReactElement {
    const colorScheme = useColorScheme()

    // Remember to subscribe to the customer elsewhere!
    const ownerHouse = useSelector(selectThumbnail(itemKey, itemKey))

    const [firstName, lastName] = customerName.split(' ')
    const fInitial = firstName ? firstName[0] : ''
    const lInitial = lastName ? lastName[0] : ''

    const customerAddressFormatted = customerAddress
      .replace(', EE.UU', '')
      .replace(', USA', '')
      .replace(', United States', '')
      .replace(', EE. UU.', '')
      .replace(', US', '')

    const avatarNode =
      !hideAvatar &&
      (ownerHouse ? (
        <rn.Image
          style={styles.avatar}
          source={{
            uri: ownerHouse,
          }}
        />
      ) : (
        <rn.View style={styles.avatar}>
          <Text
            size={26}
            style={colorScheme === 'dark' ? styles.fontWhite : styles.fontGrey}
          >
            {`${fInitial} ${lInitial}`}
          </Text>
        </rn.View>
      ))

    return (
      <rn.View style={colorScheme === 'dark' ? styles.itemDark : styles.item}>
        {avatarNode}

        <rn.View style={gs.padH6} />

        <rn.View style={styles.info}>
          <rn.View>
            <Text bold numberOfLines={1} size={18}>
              {customerName}
            </Text>

            <rn.View style={gs.padV1} />

            <Text numberOfLines={1} size={14}>
              {customerAddressFormatted}
            </Text>
          </rn.View>

          <rn.View style={styles.lowerHalf}>
            <rn.View>
              {c.CODENAME === 'nirvana' ? (
                <rn.View />
              ) : (
                <rn.View style={gs.rowCentered}>
                  <Icon color="rgb(100,100,100)" name="phone" size={12} />
                  <rn.View style={gs.padH2} />
                  <Text size={12}>{setter}</Text>
                </rn.View>
              )}

              <rn.View style={gs.padV1} />

              <rn.View style={gs.rowCentered}>
                <Icon color="rgb(100,100,100)" name="gavel" size={12} />
                <rn.View style={gs.padH2} />
                <Text size={12}>{closer}</Text>
              </rn.View>
            </rn.View>

            <rn.View style={gs.rowCentered}>
              <Icon color="rgb(100,100,100)" name="office-building" size={12} />
              <rn.View style={gs.padH2} />
              <Text size={12}>{solarCompany}</Text>
            </rn.View>
          </rn.View>
        </rn.View>
      </rn.View>
    )
  },
)

const titleTextBase = {
  fontFamily: c.light.fontFamily,
  fontSize: 18,
  fontWeight: '500',
} as const

const itemBase = {
  backgroundColor: 'white',
  borderColor: 'rgba(0,0,0,0.2)',
  borderRadius: 16,
  flex: 1,
  flexDirection: 'row',
  marginHorizontal: 12,
  marginVertical: c.IS_WEB ? 0 : 8,
  padding: paddingVertical,
  ...gs.shadowHalf,
} as const

const styles = StyleSheet.create({
  avatar: {
    height: avatarEdge,
    width: avatarEdge,
    backgroundColor: 'rgba(0,0,0,0.1)',
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  darkBg: {
    backgroundColor: '#2E2A27',
  },
  info: {
    flexGrow: 1,
    flexShrink: 1,
    justifyContent: 'space-between',
  },
  fontGrey: {
    fontSize: 26,
    color: '#9F9F9F',
  },
  fontWhite: {
    fontSize: 26,
    color: 'white',
  },
  housePicture: {
    backgroundColor: '#2E2A27',
    borderRadius: 8,
  },
  lowerHalf: { ...gs.rowSpaceBetween, alignItems: 'flex-end' },
  item: itemBase,
  itemDark: {
    ...itemBase,
    borderColor: 'rgba(255,255,255,0.2)',
    shadowColor: '#000',
    backgroundColor: '#3C3834',
  },
  titleText: titleTextBase,
  titleTextDark: {
    ...titleTextBase,
    color: 'white',
  },
})
